import type * as TwilioSdk from "@twilio/voice-sdk";
import type { Call, Device } from "@twilio/voice-sdk";
import { ApiError, api, post } from "../api/client";
import type { VoiceToken } from "../api/types";
import { useVoice } from "./store";

const HEARTBEAT_MS = 30_000;
const REGISTER_TIMEOUT_MS = 15_000;
/** How long an outgoing call may stay "connecting" before we give up (Twilio normally answers in 1–3 s). */
export const CONNECT_TIMEOUT_MS = 30_000;

/**
 * Owns the Twilio Voice SDK Device for this browser tab. The server remains the source of truth for
 * call state; this class only reflects the local media leg (connect, accept, mute, DTMF, hang up).
 */
class VoiceManager {
  private device: Device | null = null;
  private calls = new Map<string, Call>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private starting: Promise<void> | null = null;
  private sdk: typeof TwilioSdk | null = null;

  private get state() {
    return useVoice.getState();
  }

  get audio() {
    return this.device?.audio ?? null;
  }

  isSupported(): boolean {
    return typeof window !== "undefined" && !!window.RTCPeerConnection && !!navigator.mediaDevices?.getUserMedia;
  }

  /** Incremented by stop(); an in-flight start from an older generation abandons itself. */
  private generation = 0;
  private startingGeneration = -1;

  start(): Promise<void> {
    if (this.starting && this.startingGeneration === this.generation) return this.starting;
    const gen = this.generation;
    this.startingGeneration = gen;
    const attempt = this.doStart(gen).finally(() => {
      if (this.starting === attempt) this.starting = null;
    });
    this.starting = attempt;
    return attempt;
  }

  private async doStart(gen: number) {
    if (this.device) return;
    if (!this.isSupported()) {
      this.state.set({ status: "unsupported", error: "This browser does not support WebRTC calling." });
      return;
    }
    this.state.set({ status: "connecting", error: null });
    let token: VoiceToken;
    try {
      token = await post<VoiceToken>("/voice/token");
    } catch (err) {
      this.state.set({ status: "error", error: err instanceof ApiError ? err.message : "Could not obtain a calling token." });
      return;
    }
    // The SDK is loaded on demand so users without calling permissions never download it.
    const sdk = await import("@twilio/voice-sdk");
    if (gen !== this.generation) return; // stopped while we were fetching the token or SDK
    const { Device: DeviceCtor, Call: CallNs } = sdk;
    this.sdk = sdk;
    const device = new DeviceCtor(token.token, {
      codecPreferences: [CallNs.Codec.Opus, CallNs.Codec.PCMU],
      closeProtection: "You are on a call. Leaving will hang up.",
      tokenRefreshMs: 30_000,
      allowIncomingWhileBusy: true,
      logLevel: "error",
    });
    this.device = device;
    this.state.set({ identity: token.identity, incomingAllowed: token.incomingAllowed, outgoingAllowed: token.outgoingAllowed });

    device.on(sdk.Device.EventName.Registered, () => {
      this.state.set({ status: "ready", registered: true, error: null });
      void this.sendHeartbeat();
    });
    device.on(sdk.Device.EventName.Unregistered, () => {
      this.state.set({ registered: false, status: this.device ? "offline" : "idle" });
    });
    device.on(sdk.Device.EventName.Error, (error: { message?: string; code?: number }) => {
      // Token/auth errors are fatal; any error before the device is usable means it cannot connect.
      const fatal = (error.code !== undefined && error.code >= 20101 && error.code <= 20160) || this.state.status !== "ready";
      this.state.set({ error: `${error.message ?? "Voice error"}${error.code ? ` (${error.code})` : ""}`, ...(fatal ? { status: "error" } : {}) });
    });
    device.on(sdk.Device.EventName.TokenWillExpire, () => void this.refreshToken());
    device.on(sdk.Device.EventName.Incoming, (call: Call) => this.onIncoming(call));
    device.audio?.on("deviceChange", () => {
      // Consumers re-read device lists from the manager; bump state to trigger a render.
      this.state.set({});
    });

    if (token.incomingAllowed) {
      try {
        await Promise.race([
          device.register(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Could not reach the calling service. Check your network connection.")), REGISTER_TIMEOUT_MS)),
        ]);
      } catch (err) {
        if (gen === this.generation) this.state.set({ status: "error", error: (err as Error).message || "Could not register for incoming calls." });
        return;
      }
      if (gen !== this.generation) return;
      this.heartbeat = setInterval(() => void this.sendHeartbeat(), HEARTBEAT_MS);
    } else {
      this.state.set({ status: "ready" });
    }
  }

  async stop(): Promise<void> {
    this.generation++;
    this.starting = null;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    const wasRegistered = this.state.registered;
    this.device?.destroy();
    this.device = null;
    this.calls.clear();
    this.state.set({ status: "idle", error: null, registered: false, incoming: [], activeCallSid: null, sdkCallState: null, muted: false, identity: null });
    if (wasRegistered) {
      await api("/voice/presence", { method: "POST", body: { presence: this.state.presence, registered: false }, keepalive: true }).catch(() => undefined);
    }
  }

  private async refreshToken() {
    try {
      const token = await post<VoiceToken>("/voice/token");
      this.device?.updateToken(token.token);
    } catch (err) {
      this.state.set({ status: "error", error: err instanceof ApiError && err.status === 401 ? "Your session expired. Sign in again to keep calling." : "Could not renew the calling token." });
    }
  }

  async sendHeartbeat() {
    if (!this.state.incomingAllowed) return;
    try {
      await post("/voice/presence", { presence: this.state.presence, registered: !!this.sdk && this.device?.state === this.sdk.Device.State.Registered });
    } catch {
      // Presence is best-effort; registration errors are reported through device events.
    }
  }

  async setPresence(presence: "AVAILABLE" | "AWAY") {
    this.state.set({ presence });
    await this.sendHeartbeat();
  }

  private bindCall(call: Call, callSid: () => string | undefined) {
    call.on("accept", () => {
      const sid = callSid();
      if (sid) this.calls.set(sid, call);
      this.state.set({ activeCallSid: sid ?? null, sdkCallState: "open", muted: call.isMuted(), lastCallError: null });
    });
    call.on("ringing", () => this.state.set({ sdkCallState: "ringing" }));
    call.on("mute", (muted: boolean) => this.state.set({ muted }));
    call.on("reconnecting", () => this.state.set({ sdkCallState: "reconnecting", callWarning: "Connection lost — reconnecting…" }));
    call.on("reconnected", () => this.state.set({ sdkCallState: "open", callWarning: null }));
    call.on("warning", (name: string) => this.state.set({ callWarning: `Call quality issue detected (${name}).` }));
    call.on("warning-cleared", () => this.state.set({ callWarning: null }));
    const end = (error?: string) => {
      const sid = callSid();
      if (sid) this.calls.delete(sid);
      const s = this.state;
      this.state.set({
        incoming: s.incoming.filter((i) => i.callSid !== sid),
        ...(s.activeCallSid === sid || !s.activeCallSid ? { activeCallSid: null, sdkCallState: null, muted: false, callWarning: null } : {}),
        ...(error ? { lastCallError: error } : {}),
      });
    };
    call.on("disconnect", () => end());
    call.on("cancel", () => end());
    call.on("reject", () => end());
    call.on("error", (e: { message?: string }) => end(e.message ?? "Call error"));
  }

  private onIncoming(call: Call) {
    const callSid = call.parameters.CallSid;
    if (!callSid) return;
    this.calls.set(callSid, call);
    this.bindCall(call, () => callSid);
    this.state.set({
      incoming: [...this.state.incoming, { callSid, from: call.parameters.From ?? "Unknown", receivedAt: Date.now() }],
    });
  }

  /** Connects the browser leg for an outbound call previously authorized by POST /calls/outbound. */
  async connectOutbound(callId: string): Promise<void> {
    if (!this.device) throw new Error("Softphone is not connected");
    this.state.set({ sdkCallState: "connecting", lastCallError: null });
    const call = await this.device.connect({ params: { CallId: callId } });
    let sid: string | undefined;
    this.bindCall(call, () => sid ?? call.parameters.CallSid);
    // If Twilio never picks the call up (e.g. the TwiML App points at another server), don't spin forever.
    const watchdog = setTimeout(() => {
      call.disconnect();
      void post(`/calls/${callId}/end`).catch(() => undefined); // release it on the server too
      this.state.set({
        sdkCallState: null, activeCallSid: null, muted: false,
        lastCallError: "The call couldn't be connected. Twilio didn't respond; check the TwiML App's Voice URL points to this server, then try again.",
      });
    }, CONNECT_TIMEOUT_MS);
    const settle = () => clearTimeout(watchdog);
    call.on("accept", settle);
    call.on("disconnect", settle);
    call.on("cancel", settle);
    call.on("reject", settle);
    call.on("error", settle);
    call.on("accept", () => {
      sid = call.parameters.CallSid;
      if (sid) {
        this.calls.set(sid, call);
        this.state.set({ activeCallSid: sid });
      }
    });
  }

  accept(callSid: string) {
    const call = this.calls.get(callSid);
    if (!call) return;
    this.state.set({ incoming: this.state.incoming.filter((i) => i.callSid !== callSid), sdkCallState: "connecting" });
    call.accept();
  }

  reject(callSid: string) {
    const call = this.calls.get(callSid);
    this.state.set({ incoming: this.state.incoming.filter((i) => i.callSid !== callSid) });
    call?.reject();
  }

  hangup() {
    const sid = this.state.activeCallSid;
    const call = sid ? this.calls.get(sid) : null;
    if (call) call.disconnect();
    else this.device?.disconnectAll();
  }

  toggleMute() {
    const sid = this.state.activeCallSid;
    const call = sid ? this.calls.get(sid) : null;
    if (call) call.mute(!call.isMuted());
  }

  sendDigits(digits: string) {
    const sid = this.state.activeCallSid;
    const call = sid ? this.calls.get(sid) : null;
    call?.sendDigits(digits);
  }

  hasMediaFor(callSid: string | null | undefined): boolean {
    return !!callSid && this.calls.has(callSid);
  }
}

export const voice = new VoiceManager();
